import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Camera, Upload, X, MapPin, Loader2, Eye, Download } from "lucide-react";
import { toast } from "sonner";

function useToken() {
  return typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;
}

function Lightbox({ path, alt, onClose }) {
  const token = useToken();
  if (!path) return null;
  const url = `${API}/files/${path}?auth=${token}`;
  return (
    <Dialog open onOpenChange={(v)=>{ if(!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] p-2 bg-slate-950 border-slate-800 overflow-hidden">
        <div className="relative">
          <img src={url} alt={alt||"preview"} className="w-full max-h-[85vh] object-contain rounded" data-testid="photo-lightbox"/>
          <div className="absolute top-2 right-2 flex gap-2">
            <a href={url} target="_blank" rel="noopener noreferrer" download className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2" data-testid="lightbox-download">
              <Download className="w-4 h-4"/>
            </a>
            <button onClick={onClose} className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2" data-testid="lightbox-close">
              <X className="w-4 h-4"/>
            </button>
          </div>
          {alt && <div className="absolute bottom-2 left-2 text-white/90 text-xs bg-black/50 rounded px-2 py-1">{alt}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Single photo capture/upload. Value = storage_path (string) or null.
 * onCapture(path, gps?) with optional {lat,lng}.
 */
export function PhotoField({ label, value, onChange, testid, withGps = false }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const token = useToken();
  const url = value ? `${API}/files/${value}?auth=${token}` : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let gps = null;
      if (withGps && navigator.geolocation) {
        try {
          gps = await new Promise((res) => navigator.geolocation.getCurrentPosition(
            (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
            () => res(null), { timeout: 8000, enableHighAccuracy: true }
          ));
        } catch { gps = null; }
      }
      onChange(data.path, gps);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error("Upload failed — " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
        {label} {withGps && <MapPin className="w-3 h-3 text-emerald-600" />}
      </div>
      {value ? (
        <div className="relative inline-block group">
          <button type="button" onClick={()=>setShowLightbox(true)} className="block" data-testid={`${testid}-view`}>
            <img src={url} alt={label} className="w-28 h-28 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-amber-400 transition cursor-zoom-in" data-testid={`${testid}-preview`}/>
          </button>
          <div className="absolute -top-2 -right-2 flex gap-1">
            <button type="button" onClick={()=>setShowLightbox(true)} className="bg-slate-900 text-white rounded-full p-1 shadow" data-testid={`${testid}-view-btn`}>
              <Eye className="w-3 h-3"/>
            </button>
            <button type="button" onClick={() => onChange(null)} className="bg-rose-500 text-white rounded-full p-1 shadow" data-testid={`${testid}-remove`}>
              <X className="w-3 h-3" />
            </button>
          </div>
          {showLightbox && <Lightbox path={value} alt={label} onClose={()=>setShowLightbox(false)}/>}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>upload(e.target.files?.[0])} />
          <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={(e)=>upload(e.target.files?.[0])} />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={()=>camRef.current?.click()} data-testid={`${testid}-camera`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1"/>}
            {busy ? "" : "Camera"}
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={()=>galRef.current?.click()} data-testid={`${testid}-gallery`}>
            <Upload className="w-3.5 h-3.5 mr-1"/>Upload
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * FileField — like PhotoField but accepts BOTH images and PDFs.
 * PDFs display as an icon card with an "Open" link; images use the normal preview + lightbox.
 */
export function FileField({ label, value, onChange, testid }) {
  const inpRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const token = useToken();
  const url = value ? `${API}/files/${value}?auth=${token}` : null;
  const isPdf = value && String(value).toLowerCase().endsWith(".pdf");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error("Upload failed — " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      {value ? (
        <div className="relative inline-block group">
          {isPdf ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="w-28 h-28 flex flex-col items-center justify-center border border-slate-200 rounded-lg bg-rose-50 text-rose-700 hover:ring-2 hover:ring-amber-400" data-testid={`${testid}-view`}>
              <Download className="w-8 h-8 mb-1"/>
              <div className="text-[10px] font-mono">PDF</div>
              <div className="text-[9px] opacity-70">Click to open</div>
            </a>
          ) : (
            <button type="button" onClick={()=>setShowLightbox(true)} className="block" data-testid={`${testid}-view`}>
              <img src={url} alt={label} className="w-28 h-28 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-amber-400 transition cursor-zoom-in"/>
            </button>
          )}
          <button type="button" onClick={() => onChange(null)} className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 shadow" data-testid={`${testid}-remove`}>
            <X className="w-3 h-3" />
          </button>
          {showLightbox && !isPdf && <Lightbox path={value} alt={label} onClose={()=>setShowLightbox(false)}/>}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <input ref={inpRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e)=>upload(e.target.files?.[0])} />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={()=>inpRef.current?.click()} data-testid={`${testid}-upload`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5 mr-1"/>}
            {busy ? "" : "Upload PDF/Image"}
          </Button>
        </div>
      )}
    </div>
  );
}
export function PhotoGallery({ label, value = [], onChange, testid, max = 8 }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lbPath, setLbPath] = useState(null);
  const token = useToken();

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const arr = [...value];
    for (const f of files) {
      if (arr.length >= max) break;
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        arr.push(data.path);
      } catch (e) { toast.error("Upload failed"); }
    }
    onChange(arr);
    setBusy(false);
  };

  return (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1">{label} <span className="text-slate-400">({value.length}/{max})</span></div>
      <div className="flex flex-wrap gap-2 items-center">
        {value.map((p) => (
          <div key={p} className="relative group">
            <button type="button" onClick={()=>setLbPath(p)}><img src={`${API}/files/${p}?auth=${token}`} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-amber-400 cursor-zoom-in"/></button>
            <button type="button" onClick={()=>onChange(value.filter(x=>x!==p))} className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5 shadow group-hover:scale-110 transition">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <>
            <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e)=>upload(Array.from(e.target.files||[]))} />
            <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={(e)=>upload(Array.from(e.target.files||[]))} />
            <button type="button" onClick={()=>camRef.current?.click()} disabled={busy}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-amber-400 hover:bg-amber-50 flex flex-col items-center justify-center text-slate-500" data-testid={`${testid}-camera`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Camera className="w-4 h-4"/>}
              <span className="text-[10px] mt-1">Camera</span>
            </button>
            <button type="button" onClick={()=>galRef.current?.click()} disabled={busy}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-amber-400 hover:bg-amber-50 flex flex-col items-center justify-center text-slate-500" data-testid={`${testid}-gallery`}>
              <Upload className="w-4 h-4"/>
              <span className="text-[10px] mt-1">Upload</span>
            </button>
          </>
        )}
      </div>
      {lbPath && <Lightbox path={lbPath} alt={label} onClose={()=>setLbPath(null)}/>}
    </div>
  );
}
